import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {renderToStaticMarkup} from "react-dom/server";
import Highlighter from "react-highlight-words"
import EmailContent from './EmailContent'
import MsgReader from '@kenjiuno/msgreader'
import { Buffer } from 'buffer'

// Make Buffer available globally
window.Buffer = Buffer;

const tidyEmailBody = (textBody) => {
  if (!textBody) return [];
  
  // Split the body into sections based on common email separators
  const sections = textBody.split(/\n-{2,}\s*\n/);
  
  // If no sections were found, return the whole body as one section
  if (sections.length === 1) {
    return [textBody];
  }
  
  return sections;
}

const MsgViewer = props => {
  const { blob, ...rest} = props

  const [email, setEmail] = useState(null)

  const toHtmlBody = (textBody) => {
    const emailBodySections = tidyEmailBody(textBody)
    return renderToStaticMarkup(<>{emailBodySections.map((section,i) => <React.Fragment key={i}><div style={{whiteSpace: 'pre-line'}}><Highlighter
      highlightTag={({ children, highlightIndex }) => (<strong>{children}</strong>)}
      searchWords={[/(\n\s*|^)(To|From|Date|Subject|Sent|Cc|Bcc):/]}
      textToHighlight={section.trim()}
    /></div>{(i < emailBodySections.length-1) && <><br/><hr/><br/></>}</React.Fragment>)}</>);
  }

  useEffect(()=>{
    if (!blob) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const msgFileBuffer = reader.result;
        const msg = new MsgReader(msgFileBuffer);
        const info = msg.getFileData();
        
        const newEmail = {
          subject: info.subject,
          from: {name: info.senderName, email: info.senderEmail},
          to: info.recipients.map(recipient => ({name: recipient.name, email: recipient.email})),
          body: toHtmlBody(info.body),
          attachments: info.attachments.map(attachment => {
            const name = attachment.fileName || attachment.fileNameShort;
            const extension = name.split('.').pop().toLowerCase();
            const isImage = ['gif', 'png', 'jpg', 'jpeg'].includes(extension);
            const isInline = isImage && (attachment.contentLength < 30000);
            
            // Always try to get the attachment content
            let url = null;
            try {
              const attachmentData = msg.getAttachment(attachment);
              if (attachmentData && attachmentData.content) {
                const mimeType = extension === 'pdf' ? 'application/pdf' : 
                               isImage ? `image/${extension}` : 
                               'application/octet-stream';
                const blob = new Blob([attachmentData.content], {type: mimeType});
                url = URL.createObjectURL(blob);
              }
            } catch (error) {
              console.warn('Failed to process attachment:', name, error);
            }
            
            return {
              name: name,
              size: attachment.contentLength,
              contentId: attachment.dataId,
              isInline: isInline,
              fetchURL: url,
              downloadURL: url,
              contentType: isImage ? `image/${extension}` : 
                          extension === 'pdf' ? 'application/pdf' : 
                          'application/octet-stream',
              forceDownload: true
            };
          }).filter(a => a && a.downloadURL) // Only include attachments we successfully processed
        };
        
        setEmail(newEmail);
      } catch (error) {
        console.error('Error processing MSG file:', error);
      }
    };
    reader.readAsArrayBuffer(blob);
  }, [blob]);

  return email ? <EmailContent email={email} /> : null
}

MsgViewer.propTypes = {
  blob: PropTypes.object.isRequired,
}

export default MsgViewer
